/**
 * Spot Market Maker Handler
 * Browser-based spot market maker with VWAP pricing and queue ratio optimization
 *
 * Ported from usdh.py for the mm4 web client
 * Supports multiple spot pairs (USDH/USDC, etc.)
 */

const MIN_ORDER_VALUE_USD = 10.5;  // Hyperliquid spot minimum is $10
const BALANCE_BUFFER = 0.998;       // 0.2% buffer for fees
const API_TIMEOUT_MS = 15000;       // 15s timeout for API calls (Fix #3)
const MIN_API_INTERVAL_MS = 100;    // 100ms minimum between API calls (Fix #1)

// Stablecoin tokens for total balance calculation (all assumed 1:1 USD)
const STABLECOIN_TOKENS = ['USDC', 'USDH', 'USDE', 'USDT0'];

// WebSocket URL
const WS_URL = 'wss://api.hyperliquid.xyz/ws';

export class SpotHandler {
    constructor() {
        this.hlClient = null;           // BrowserHyperliquidClient reference
        this.positionTracker = null;    // BrowserPositionTracker reference
        this.enabled = false;           // Global enable flag

        // Config per symbol: symbol -> config
        this.configs = new Map();

        // State: symbol -> { orderbook, lastRebalance, lastVwap, candles }
        this.state = new Map();

        // Spot metadata caching for flexible token resolution
        this._spotMeta = null;          // Cached spotMeta response
        this._tokenCache = new Map();   // token index -> token name
        this._pairTokenCache = new Map(); // symbol -> { baseToken, quoteToken }

        // Total stablecoin balance cache (for percentage calculation)
        this._totalStablecoinBalance = 0;  // Sum of USDC + USDH + USDE + USDT0
        this._lastBalanceFetch = 0;        // Timestamp of last fetch

        // Circular log buffer
        this.logs = [];
        this.maxLogs = 500;

        // Timing - WebSocket primary, REST fallback with wider intervals
        this.loopDelay = 5000;          // 5s main loop (reduced from 0.5s since WS handles updates)
        this.pollInterval = 10000;      // 10s balance poll
        this.rebalanceInterval = 10000; // 10s rebalance check
        this.orderbookInterval = 300000; // 5 min REST fallback (WS is primary)
        this.vwapInterval = 3600000;    // 1 hour REST fallback for VWAP

        // WebSocket state
        this._ws = null;
        this._wsConnected = false;
        this._wsSubscriptions = new Map();  // symbol -> { l2Book: bool, candle: bool }
        this._wsReconnectAttempts = 0;
        this._wsMaxReconnectAttempts = 5;
        this._wsReconnectDelay = 1000;      // Start with 1s, exponential backoff
        this._wsPendingMessages = [];       // Messages to send after reconnect

        // Runtime
        this._loopInterval = null;
        this._isRunning = false;
        this._loopInProgress = false;  // Fix #5: Mutex to prevent concurrent loop execution
        this._lastApiCall = 0;          // Fix #1: Rate limiting timestamp
        this._debugEnabled = false;     // Fix #15: Configurable debug logging
        
        // Persistence
        this._isRestoring = false;      // Flag to prevent saveState during restoration
    }

    /**
     * Initialize with Hyperliquid client
     * @param {BrowserHyperliquidClient} hlClient
     * @param {BrowserPositionTracker} positionTracker
     */
    async initialize(hlClient, positionTracker = null) {
        this.hlClient = hlClient;
        this.positionTracker = positionTracker || window.BrowserPositionTracker;

        if (!this.hlClient || !this.hlClient.isInitialized) {
            throw new Error('[SPOT] Hyperliquid client not initialized');
        }

        this._log('SpotHandler initialized');
        
        // Restore state from previous session
        this.restoreState();
        
        return true;
    }

    // === FIX #1: Rate Limiting ===

    /**
     * Rate-limited API call wrapper
     * Ensures minimum interval between API calls to prevent overload
     */
    async _rateLimitedCall(apiCall) {
        const now = Date.now();
        const elapsed = now - this._lastApiCall;
        if (elapsed < MIN_API_INTERVAL_MS) {
            await this._sleep(MIN_API_INTERVAL_MS - elapsed);
        }
        this._lastApiCall = Date.now();
        return apiCall();
    }

    // === FIX #3: API Timeout Wrapper ===

    /**
     * Wrap an API call with timeout
     * @param {Promise} promise - API call promise
     * @param {number} timeoutMs - Timeout in milliseconds
     * @param {string} operationName - Name for error message
     */
    async _withTimeout(promise, timeoutMs = API_TIMEOUT_MS, operationName = 'API call') {
        let timeoutId;
        const timeoutPromise = new Promise((_, reject) => {
            timeoutId = setTimeout(() => {
                reject(new Error(`${operationName} timed out after ${timeoutMs}ms`));
            }, timeoutMs);
        });

        try {
            const result = await Promise.race([promise, timeoutPromise]);
            clearTimeout(timeoutId);
            return result;
        } catch (error) {
            clearTimeout(timeoutId);
            throw error;
        }
    }

    /**
     * Sleep utility
     */
    _sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    // === WEBSOCKET MANAGEMENT ===

    /**
     * Initialize WebSocket connection
     */
    _initWebSocket() {
        if (this._ws && this._wsConnected) {
            return; // Already connected
        }

        this._log('[WS] Connecting to Hyperliquid WebSocket...');

        try {
            this._ws = new WebSocket(WS_URL);

            this._ws.onopen = () => this._onWsOpen();
            this._ws.onmessage = (event) => this._onWsMessage(event);
            this._ws.onclose = (event) => this._onWsClose(event);
            this._ws.onerror = (error) => this._onWsError(error);
        } catch (error) {
            this._log(`[WS] Failed to create WebSocket: ${error.message}`);
        }
    }

    /**
     * Handle WebSocket open
     */
    _onWsOpen() {
        // this._log('[WS] Connected');
        this._wsConnected = true;
        this._wsReconnectAttempts = 0;
        this._wsReconnectDelay = 1000;

        // Send any pending messages
        while (this._wsPendingMessages.length > 0) {
            const msg = this._wsPendingMessages.shift();
            this._wsSend(msg);
        }

        // Resubscribe to all symbols
        for (const symbol of this.configs.keys()) {
            this._subscribeSymbol(symbol);
        }
    }

    /**
     * Handle WebSocket message
     */
    _onWsMessage(event) {
        try {
            const data = JSON.parse(event.data);

            // Handle subscription confirmation
            if (data.channel === 'subscriptionResponse') {
                this._debug(`[WS] Subscription confirmed: ${JSON.stringify(data.data)}`);
                return;
            }

            // Handle l2Book updates
            if (data.channel === 'l2Book') {
                this._handleL2BookUpdate(data.data);
                return;
            }

            // Handle candle updates
            if (data.channel === 'candle') {
                this._handleCandleUpdate(data.data);
                return;
            }

            // Handle user events (fills, order updates)
            if (data.channel === 'user') {
                this._handleUserEvent(data.data);
                return;
            }

        } catch (error) {
            this._debug(`[WS] Error parsing message: ${error.message}`);
        }
    }

    /**
     * Handle WebSocket close with reconnection
     */
    _onWsClose(event) {
        this._wsConnected = false;
        this._log(`[WS] Disconnected (code: ${event.code})`);

        // Attempt reconnection if still running
        if (this._isRunning && this._wsReconnectAttempts < this._wsMaxReconnectAttempts) {
            this._wsReconnectAttempts++;
            const delay = this._wsReconnectDelay * Math.pow(2, this._wsReconnectAttempts - 1);
            this._log(`[WS] Reconnecting in ${delay}ms (attempt ${this._wsReconnectAttempts}/${this._wsMaxReconnectAttempts})`);

            setTimeout(() => {
                if (this._isRunning) {
                    this._initWebSocket();
                }
            }, delay);
        } else if (this._wsReconnectAttempts >= this._wsMaxReconnectAttempts) {
            this._log('[WS] Max reconnection attempts reached, falling back to REST polling');
        }
    }

    /**
     * Handle WebSocket error
     */
    _onWsError(error) {
        this._log(`[WS] Error: ${error.message || 'Unknown error'}`);
    }

    /**
     * Send message via WebSocket
     */
    _wsSend(msg) {
        if (this._ws && this._wsConnected) {
            this._ws.send(JSON.stringify(msg));
            return true;
        } else {
            // Queue message for later
            this._wsPendingMessages.push(msg);
            return false;
        }
    }

    /**
     * Subscribe to l2Book and candle for a symbol
     */
    _subscribeSymbol(symbol) {
        if (!this._wsConnected) {
            this._debug(`[WS] Not connected, queueing subscriptions for ${symbol}`);
            return;
        }

        const subs = this._wsSubscriptions.get(symbol) || { l2Book: false, candle: false };

        // Subscribe to l2Book
        if (!subs.l2Book) {
            this._wsSend({
                method: 'subscribe',
                subscription: { type: 'l2Book', coin: symbol }
            });
            subs.l2Book = true;
            // this._log(`[WS] Subscribed to l2Book for ${symbol}`);
        }

        // Subscribe to 1h candles for VWAP
        if (!subs.candle) {
            this._wsSend({
                method: 'subscribe',
                subscription: { type: 'candle', coin: symbol, interval: '1h' }
            });
            subs.candle = true;
            // this._log(`[WS] Subscribed to 1h candles for ${symbol}`);
        }

        this._wsSubscriptions.set(symbol, subs);
    }

    /**
     * Unsubscribe from a symbol
     */
    _unsubscribeSymbol(symbol) {
        if (!this._wsConnected) return;

        const subs = this._wsSubscriptions.get(symbol);
        if (!subs) return;

        if (subs.l2Book) {
            this._wsSend({
                method: 'unsubscribe',
                subscription: { type: 'l2Book', coin: symbol }
            });
        }

        if (subs.candle) {
            this._wsSend({
                method: 'unsubscribe',
                subscription: { type: 'candle', coin: symbol, interval: '1h' }
            });
        }

        this._wsSubscriptions.delete(symbol);
        this._log(`[WS] Unsubscribed from ${symbol}`);
    }

    /**
     * Handle l2Book update from WebSocket
     */
    _handleL2BookUpdate(data) {
        const symbol = data.coin;
        const state = this.state.get(symbol);
        if (!state) return;

        // Parse levels: data.levels = [[bids], [asks]]
        const bids = (data.levels[0] || []).map(level => [
            parseFloat(level.px),
            parseFloat(level.sz)
        ]).filter(([p, s]) => Number.isFinite(p) && Number.isFinite(s) && p > 0 && s > 0);

        const asks = (data.levels[1] || []).map(level => [
            parseFloat(level.px),
            parseFloat(level.sz)
        ]).filter(([p, s]) => Number.isFinite(p) && Number.isFinite(s) && p > 0 && s > 0);

        state.orderbook = { bids, asks };
        state.lastOrderbook = Date.now();

        if (bids.length > 0 && asks.length > 0) {
            this._debug(`[WS] ${symbol} orderbook: ${bids[0][0].toFixed(5)}/${asks[0][0].toFixed(5)}`);
        }
    }

    /**
     * Handle candle update from WebSocket
     * Accumulates candles for VWAP calculation
     */
    _handleCandleUpdate(data) {
        const symbol = data.s; // symbol
        const state = this.state.get(symbol);
        if (!state) return;

        // Initialize candles array if needed
        if (!state.candles) {
            state.candles = [];
        }

        // Parse candle: { t, T, s, i, o, c, h, l, v, n }
        const candle = {
            t: data.t,      // open time
            o: parseFloat(data.o),
            h: parseFloat(data.h),
            l: parseFloat(data.l),
            c: parseFloat(data.c),
            v: parseFloat(data.v)
        };

        // Update or append candle
        const existingIdx = state.candles.findIndex(c => c.t === candle.t);
        if (existingIdx >= 0) {
            state.candles[existingIdx] = candle;
        } else {
            state.candles.push(candle);
            // Keep only last vwapHours worth of candles
            const config = this.configs.get(symbol);
            const maxCandles = config?.vwapHours || 168;
            if (state.candles.length > maxCandles) {
                state.candles = state.candles.slice(-maxCandles);
            }
        }

        this._debug(`[WS] ${symbol} candle update: close=${candle.c.toFixed(5)}, vol=${candle.v.toFixed(0)}`);
    }

    /**
     * Handle user events (fills, order updates)
     */
    _handleUserEvent(data) {
        // Log fills for visibility
        if (data.fills && data.fills.length > 0) {
            for (const fill of data.fills) {
                this._log(`[FILL] ${fill.coin}: ${fill.side} ${fill.sz} @ ${fill.px}`);
            }
        }
    }

    /**
     * Close WebSocket connection
     */
    _closeWebSocket() {
        if (this._ws) {
            this._ws.onclose = null; // Prevent reconnection
            this._ws.close();
            this._ws = null;
            this._wsConnected = false;
            this._wsSubscriptions.clear();
            this._log('[WS] Closed');
        }
    }

    // === FLEXIBLE TOKEN RESOLUTION ===

    /**
     * Load spot metadata and cache token mappings
     * Uses BrowserHyperliquidClient.getSpotMeta() which has permanent caching
     */
    async _loadSpotMeta() {
        if (this._spotMeta) {
            return; // Already loaded
        }

        try {
            // Use BrowserHyperliquidClient's cached getSpotMeta() method
            this._spotMeta = await this._rateLimitedCall(() =>
                this._withTimeout(
                    this.hlClient.getSpotMeta(),
                    API_TIMEOUT_MS,
                    'getSpotMeta'
                )
            );

            // Build token index -> name cache
            if (this._spotMeta && this._spotMeta.tokens) {
                for (const token of this._spotMeta.tokens) {
                    this._tokenCache.set(token.index, token.name);
                }
                // this._log(`[META] Loaded ${this._tokenCache.size} spot tokens`);
            }
        } catch (error) {
            this._log(`[META] Error loading spotMeta: ${error.message}`);
            throw error;
        }
    }

    /**
     * Fetch total stablecoin balance (USDC + USDH + USDE + USDT0)
     * All stablecoins are assumed 1:1 USD value
     * @returns {number} Total stablecoin balance in USD
     */
    async _fetchTotalStablecoinBalance() {
        try {
            const spotState = await this._rateLimitedCall(() =>
                this._withTimeout(
                    this.hlClient.infoClient.spotClearinghouseState({ user: this.hlClient.publicAddress }),
                    API_TIMEOUT_MS,
                    'spotClearinghouseState'
                )
            );

            let total = 0;
            if (spotState && spotState.balances) {
                for (const bal of spotState.balances) {
                    if (STABLECOIN_TOKENS.includes(bal.coin)) {
                        // Include total (not just available) for percentage calculation
                        total += parseFloat(bal.total || '0');
                    }
                }
            }

            this._totalStablecoinBalance = total;
            this._lastBalanceFetch = Date.now();
            // this._log(`[BALANCE] Total stablecoin balance: $${total.toFixed(2)} (${STABLECOIN_TOKENS.join('+')})`);  // Spammy - only log on errors
            return total;
        } catch (error) {
            this._log(`[BALANCE] Error fetching total stablecoin balance: ${error.message}`);
            return this._totalStablecoinBalance; // Return cached value on error
        }
    }

    /**
     * Resolve spot pair symbol to base/quote token names
     * @param {string} symbol - Spot pair in @index format (e.g., '@230') or name format
     * @returns {object} { baseToken, quoteToken } token names for balance lookup
     */
    async _resolveSpotPairTokens(symbol) {
        // Check cache first
        if (this._pairTokenCache.has(symbol)) {
            return this._pairTokenCache.get(symbol);
        }

        // Load metadata if not already loaded
        await this._loadSpotMeta();

        if (!this._spotMeta || !this._spotMeta.universe) {
            throw new Error(`Cannot resolve tokens for ${symbol}: spotMeta not available`);
        }

        let pair = null;

        // Handle @index format
        if (symbol.startsWith('@')) {
            const pairIndex = parseInt(symbol.slice(1), 10);
            if (isNaN(pairIndex)) {
                throw new Error(`Invalid symbol format: ${symbol} (expected @<number>)`);
            }
            pair = this._spotMeta.universe.find(p => p.index === pairIndex);
        } else if (symbol.includes('/')) {
            // Handle BASE/QUOTE format
            const [baseName, quoteName] = symbol.split('/');
            // Find tokens by name
            const baseToken = this._spotMeta.tokens.find(t => t.name === baseName);
            const quoteToken = this._spotMeta.tokens.find(t => t.name === quoteName);
            if (baseToken && quoteToken) {
                // Find pair with these tokens
                pair = this._spotMeta.universe.find(p =>
                    p.tokens[0] === baseToken.index && p.tokens[1] === quoteToken.index
                );
            }
        } else {
            // Try exact name match in universe
            pair = this._spotMeta.universe.find(p => p.name === symbol);
        }

        if (!pair) {
            throw new Error(`Spot pair not found: ${symbol}`);
        }

        const baseIdx = pair.tokens[0];
        const quoteIdx = pair.tokens[1];

        const baseToken = this._tokenCache.get(baseIdx);
        const quoteToken = this._tokenCache.get(quoteIdx);

        if (!baseToken || !quoteToken) {
            throw new Error(`Token names not found for ${symbol}: base=${baseIdx}, quote=${quoteIdx}`);
        }

        const result = { baseToken, quoteToken };
        this._pairTokenCache.set(symbol, result);
        // this._log(`[META] Resolved ${symbol} -> ${baseToken}/${quoteToken}`);

        return result;
    }

    // === CONFIGURATION API ===

    /**
     * Add a spot pair to manage
     * @param {string} symbol - Spot pair symbol (e.g., '@230' or 'USDH/USDC')
     * @param {Object} config - Configuration options
     */
    addSymbol(symbol, config = {}) {
        const defaultConfig = {
            // Adaptive pricing (default): mean ± 1.5σ (Bollinger-style)
            spreadBps: 1,           // Minimum spread: 1 bp per side = 2 bps total (covers 0.02% round-trip fee)
            vwapHours: 168,         // 7 days default for VWAP calculation

            // Fixed override (optional) - set both to use fixed pricing
            fixedMaxBid: null,      // Set to override VWAP (e.g., 0.9999)
            fixedMinAsk: null,      // Set to override VWAP (e.g., 1.0001)

            // Queue ratio optimization
            maxQueueRatio: 0.30,    // Max 30% of any level

            // Position sizing - use ONE of these:
            positionValue: 0,       // Fixed USD amount (0 = use all balance)
            balancePct: null,       // Percentage of balance (null = use positionValue)
            useAllBalance: true,    // Fallback: use all available

            // Status
            enabled: true
        };

        const finalConfig = { ...defaultConfig, ...config };

        // Fix #8: Validate maxQueueRatio (must be between 0 and 1)
        if (finalConfig.maxQueueRatio !== null && finalConfig.maxQueueRatio !== undefined) {
            if (finalConfig.maxQueueRatio <= 0 || finalConfig.maxQueueRatio > 1) {
                throw new Error(`Invalid maxQueueRatio: ${finalConfig.maxQueueRatio} (must be > 0 and <= 1)`);
            }
        }

        // Fix #10: Validate spreadBps (must be positive)
        if (!Number.isFinite(finalConfig.spreadBps) || finalConfig.spreadBps < 0) {
            throw new Error(`Invalid spreadBps: ${finalConfig.spreadBps} (must be >= 0)`);
        }

        // Fix #4: Validate fixed pricing doesn't create crossed market
        if (finalConfig.fixedMaxBid !== null && finalConfig.fixedMinAsk !== null) {
            if (finalConfig.fixedMaxBid >= finalConfig.fixedMinAsk) {
                throw new Error(`Invalid pricing: maxBid (${finalConfig.fixedMaxBid}) must be < minAsk (${finalConfig.fixedMinAsk})`);
            }
        }

        // Fix #6: Normalize symbol format - store original but warn if not @XXX format
        if (!symbol.startsWith('@') && !symbol.includes('/')) {
            this._log(`[WARN] Symbol '${symbol}' may need normalization. Use '@XXX' or 'BASE/QUOTE' format.`);
        }

        this.configs.set(symbol, finalConfig);

        // Initialize state
        this.state.set(symbol, {
            orderbook: { bids: [], asks: [] },
            candles: [],            // For VWAP calculation from WebSocket
            lastRebalance: 0,
            lastOrderbook: 0,
            lastVwap: 0,
            vwap: null  // Fix #12: Use null instead of 1.0 to detect uninitialized VWAP
        });

        // Subscribe to WebSocket if connected
        if (this._wsConnected) {
            this._subscribeSymbol(symbol);
        }

        this._log(`Added symbol ${symbol} with config:`, finalConfig);
        
        // Persist config
        this.saveState();
    }

    /**
     * Update config for a symbol
     * Fix #5: Now validates all config values like addSymbol()
     */
    updateConfig(symbol, newConfig) {
        const current = this.configs.get(symbol);
        if (!current) {
            throw new Error(`Symbol ${symbol} not found`);
        }

        const merged = { ...current, ...newConfig };

        // Fix #5: Validate maxQueueRatio if provided
        if (newConfig.maxQueueRatio !== undefined) {
            if (merged.maxQueueRatio <= 0 || merged.maxQueueRatio > 1) {
                throw new Error(`Invalid maxQueueRatio: ${merged.maxQueueRatio} (must be > 0 and <= 1)`);
            }
        }

        // Fix #5: Validate spreadBps if provided
        if (newConfig.spreadBps !== undefined) {
            if (!Number.isFinite(merged.spreadBps) || merged.spreadBps < 0) {
                throw new Error(`Invalid spreadBps: ${merged.spreadBps} (must be >= 0)`);
            }
        }

        // Fix #5: Validate crossed market if pricing changed
        if (newConfig.fixedMaxBid !== undefined || newConfig.fixedMinAsk !== undefined) {
            if (merged.fixedMaxBid !== null && merged.fixedMinAsk !== null) {
                if (merged.fixedMaxBid >= merged.fixedMinAsk) {
                    throw new Error(`Invalid pricing: maxBid (${merged.fixedMaxBid}) must be < minAsk (${merged.fixedMinAsk})`);
                }
            }
        }

        this.configs.set(symbol, merged);
        this._log(`Updated config for ${symbol}:`, newConfig);
        
        // Persist config
        this.saveState();
    }

    /**
     * Switch to fixed pricing
     */
    setFixedPricing(symbol, maxBid, minAsk) {
        // Fix #4: Validate fixed pricing doesn't create crossed market
        if (maxBid >= minAsk) {
            throw new Error(`Invalid pricing: maxBid (${maxBid}) must be < minAsk (${minAsk})`);
        }
        this.updateConfig(symbol, { fixedMaxBid: maxBid, fixedMinAsk: minAsk });
    }

    /**
     * Switch to VWAP pricing
     */
    useVWAPPricing(symbol) {
        this.updateConfig(symbol, { fixedMaxBid: null, fixedMinAsk: null });
    }

    /**
     * Remove a symbol
     * Fix #6: Made async to properly await _cancelAll()
     * Fix #7: Removed dead openOrders tracking
     */
    async removeSymbol(symbol) {
        // Cancel any open orders first - now properly awaited
        await this._cancelAll(symbol);

        // Unsubscribe from WebSocket
        this._unsubscribeSymbol(symbol);

        this.configs.delete(symbol);
        this.state.delete(symbol);
        this._log(`Removed symbol ${symbol}`);
        
        // Persist config
        this.saveState();
    }

    // === CORE MARKET MAKING LOGIC ===

    /**
     * Find optimal bid price based on queue ratio
     * Ported from usdh.py find_optimal_bid_price()
     */
    _findOptimalBidPrice(orderbook, ourSize, maxBid, maxQueueRatio) {
        const bids = orderbook.bids || [];
        if (!bids.length || ourSize <= 0) {
            return maxBid;
        }

        // Find first level (from best to worst) where we'd be <= maxQueueRatio
        for (let i = 0; i < bids.length; i++) {
            const [price, size] = bids[i];

            // Don't go above our max_bid limit
            if (price > maxBid) continue;

            // Skip empty levels
            if (size <= 0) continue;

            // Calculate what % we'd be if we join this level
            const ourRatio = ourSize / (size + ourSize);

            if (ourRatio <= maxQueueRatio) {
                if (i === 0) {
                    this._debug(`[QUEUE] BID: BBO ${price.toFixed(5)} is thick (${size.toFixed(0)}), ratio=${(ourRatio * 100).toFixed(1)}%, using BBO`);
                } else {
                    this._debug(`[QUEUE] BID: level ${i} @ ${price.toFixed(5)} (${size.toFixed(0)}) ratio=${(ourRatio * 100).toFixed(1)}% <= ${(maxQueueRatio * 100).toFixed(0)}%`);
                }
                return price;
            }
        }

        // No suitable level - use maxBid
        this._debug(`[QUEUE] BID: all levels thin, using maxBid=${maxBid.toFixed(5)}`);
        return maxBid;
    }

    /**
     * Find optimal ask price based on queue ratio
     * Ported from usdh.py find_optimal_ask_price()
     */
    _findOptimalAskPrice(orderbook, ourSize, minAsk, maxQueueRatio) {
        const asks = orderbook.asks || [];
        if (!asks.length || ourSize <= 0) {
            return minAsk;
        }

        // Find first level (from best to worst) where we'd be <= maxQueueRatio
        for (let i = 0; i < asks.length; i++) {
            const [price, size] = asks[i];

            // Don't go below our min_ask limit
            if (price < minAsk) continue;

            // Skip empty levels
            if (size <= 0) continue;

            // Calculate what % we'd be if we join this level
            const ourRatio = ourSize / (size + ourSize);

            if (ourRatio <= maxQueueRatio) {
                if (i === 0) {
                    this._debug(`[QUEUE] ASK: BBO ${price.toFixed(5)} is thick (${size.toFixed(0)}), ratio=${(ourRatio * 100).toFixed(1)}%, using BBO`);
                } else {
                    this._debug(`[QUEUE] ASK: level ${i} @ ${price.toFixed(5)} (${size.toFixed(0)}) ratio=${(ourRatio * 100).toFixed(1)}% <= ${(maxQueueRatio * 100).toFixed(0)}%`);
                }
                return price;
            }
        }

        // No suitable level - use minAsk
        this._debug(`[QUEUE] ASK: all levels thin, using minAsk=${minAsk.toFixed(5)}`);
        return minAsk;
    }

    /**
     * Calculate VWAP from recent candles
     * Real VWAP = Σ(typical_price × volume) / Σ(volume)
     * @param {string} symbol - Spot pair symbol
     * @returns {number} VWAP price
     */
    async _calculateVWAP(symbol) {
        try {
            // Get config for VWAP timeframe (default 168 hours = 7 days)
            const config = this.configs.get(symbol);
            const state = this.state.get(symbol);
            const vwapHours = config?.vwapHours || 168;

            let candles = [];
            let source = 'REST';

            // Try WebSocket cached candles first (if we have enough)
            if (state?.candles && state.candles.length >= Math.min(24, vwapHours)) {
                candles = state.candles;
                source = 'WebSocket';
                this._debug(`[VWAP] Using ${candles.length} cached candles from WebSocket`);
            } else {
                // Fallback to REST API
                const now = Date.now();
                const startTime = now - (vwapHours * 60 * 60 * 1000);

                candles = await this._rateLimitedCall(() =>
                    this._withTimeout(
                        this.hlClient.getCandleData(symbol, '1h', startTime, now),
                        API_TIMEOUT_MS,
                        'VWAP candle fetch'
                    )
                );
            }

            if (!candles || candles.length === 0) {
                this._log(`[VWAP] No candles for ${symbol}, using fallback`);
                return this._getVWAPFallback(symbol);
            }

            // Real VWAP = Σ(typical_price × volume) / Σ(volume)
            // typical_price = (high + low + close) / 3
            let totalPV = 0;  // price * volume
            let totalV = 0;   // volume

            for (const c of candles) {
                const h = parseFloat(c.h);
                const l = parseFloat(c.l);
                const close = parseFloat(c.c);
                const v = parseFloat(c.v);

                // Fix #9: Validate parsed values are not NaN
                if (!Number.isFinite(h) || !Number.isFinite(l) || !Number.isFinite(close) || !Number.isFinite(v)) {
                    this._debug(`[VWAP] Skipping candle with invalid data: h=${h}, l=${l}, c=${close}, v=${v}`);
                    continue;
                }

                if (v > 0) {
                    const typicalPrice = (h + l + close) / 3;
                    totalPV += typicalPrice * v;
                    totalV += v;
                }
            }

            if (totalV === 0) {
                this._log(`[VWAP] Zero volume for ${symbol}, using fallback`);
                return this._getVWAPFallback(symbol);
            }

            const vwap = totalPV / totalV;

            // Fix #9: Final NaN check
            if (!Number.isFinite(vwap)) {
                this._log(`[VWAP] Calculated VWAP is NaN/Infinite for ${symbol}, using fallback`);
                return this._getVWAPFallback(symbol);
            }

            this._log(`[VWAP] ${symbol}: ${vwap.toFixed(6)} (${source}, ${candles.length} candles, volume=${totalV.toFixed(0)})`);
            return vwap;

        } catch (error) {
            this._log(`[VWAP] Error calculating VWAP for ${symbol}: ${error.message}`);
            return this._getVWAPFallback(symbol);
        }
    }

    /**
     * Fallback VWAP using orderbook mid-price
     * Fix #12: Returns null instead of hardcoded 1.0 to prevent dangerous defaults
     */
    _getVWAPFallback(symbol) {
        const state = this.state.get(symbol);
        if (!state) {
            this._log(`[VWAP] No state for ${symbol}, cannot calculate fallback`);
            return null;  // Fix #12: Don't return 1.0 blindly
        }

        const orderbook = state.orderbook;
        if (orderbook.bids.length > 0 && orderbook.asks.length > 0) {
            const bid = orderbook.bids[0][0];
            const ask = orderbook.asks[0][0];

            // Fix #9: Validate orderbook prices
            if (!Number.isFinite(bid) || !Number.isFinite(ask) || bid <= 0 || ask <= 0) {
                this._log(`[VWAP] Invalid orderbook prices: bid=${bid}, ask=${ask}`);
                return null;
            }

            const mid = (bid + ask) / 2;
            this._log(`[VWAP] Using mid-price fallback: ${mid.toFixed(6)}`);
            return mid;
        }

        // Fix #12: Log warning and return null instead of hardcoded 1.0
        this._log(`[VWAP] No orderbook data for ${symbol}, cannot calculate VWAP`);
        return null;
    }

    /**
     * Calculate adaptive spread using Bollinger-style formula: mean ± k*stdev
     * This adapts to each stablecoin's historical price behavior
     * @param {string} symbol - Spot pair symbol
     * @returns {object} { mean, stdev, maxBid, minAsk } or null if insufficient data
     */
    async _calculateAdaptiveSpread(symbol) {
        const config = this.configs.get(symbol);
        const state = this.state.get(symbol);
        const vwapHours = config?.vwapHours || 168;  // 7 days default
        const k = 1.5;  // Bollinger multiplier (tested to fit stablecoin behavior)
        const minSpreadBps = config?.spreadBps || 1;  // Minimum spread per side

        let candles = [];

        // Try WebSocket cached candles first
        if (state?.candles && state.candles.length >= Math.min(24, vwapHours)) {
            candles = state.candles;
        } else {
            // Fallback to REST API
            try {
                const now = Date.now();
                const startTime = now - (vwapHours * 60 * 60 * 1000);
                candles = await this._rateLimitedCall(() =>
                    this._withTimeout(
                        this.hlClient.getCandleData(symbol, '1h', startTime, now),
                        API_TIMEOUT_MS,
                        'Adaptive spread candle fetch'
                    )
                );
            } catch (error) {
                this._log(`[ADAPTIVE] Error fetching candles for ${symbol}: ${error.message}`);
                return null;
            }
        }

        if (!candles || candles.length < 24) {
            this._log(`[ADAPTIVE] Insufficient candles for ${symbol}: ${candles?.length || 0}`);
            return null;
        }

        // Extract close prices
        const closes = [];
        for (const c of candles) {
            const close = parseFloat(c.c);
            if (Number.isFinite(close) && close > 0) {
                closes.push(close);
            }
        }

        if (closes.length < 24) {
            this._log(`[ADAPTIVE] Insufficient valid closes for ${symbol}: ${closes.length}`);
            return null;
        }

        // Calculate mean
        const mean = closes.reduce((sum, p) => sum + p, 0) / closes.length;

        // Calculate standard deviation
        const squaredDiffs = closes.map(p => Math.pow(p - mean, 2));
        const variance = squaredDiffs.reduce((sum, d) => sum + d, 0) / closes.length;
        const stdev = Math.sqrt(variance);

        // Bollinger-style: bid = mean - k*stdev, ask = mean + k*stdev
        let maxBid = mean - k * stdev;
        let minAsk = mean + k * stdev;

        // Ensure minimum spread (2 bps total = 1 bps per side)
        const minSpread = minSpreadBps * 2 / 10000;
        const actualSpread = minAsk - maxBid;
        if (actualSpread < minSpread) {
            const mid = (maxBid + minAsk) / 2;
            maxBid = mid - minSpread / 2;
            minAsk = mid + minSpread / 2;
        }

        const spreadBps = (minAsk - maxBid) * 10000;
        this._log(`[ADAPTIVE] ${symbol}: mean=${mean.toFixed(6)}, stdev=${stdev.toFixed(6)}, bid=${maxBid.toFixed(4)}, ask=${minAsk.toFixed(4)} (${spreadBps.toFixed(1)} bps)`);

        return { mean, stdev, maxBid, minAsk };
    }

    /**
     * Get price range (max bid, min ask) for a symbol
     * Uses fixed prices if set, otherwise calculates adaptive spread (mean ± 1.5σ)
     * Fix #12: Handles null VWAP gracefully
     */
    async _getPriceRange(symbol) {
        const config = this.configs.get(symbol);
        if (!config) {
            return { maxBid: 0, minAsk: Infinity };
        }

        // If fixed prices set, use them
        if (config.fixedMaxBid !== null && config.fixedMinAsk !== null) {
            return {
                maxBid: config.fixedMaxBid,
                minAsk: config.fixedMinAsk
            };
        }

        // Otherwise calculate adaptive spread (Bollinger-style: mean ± 1.5σ)
        const state = this.state.get(symbol);
        const now = Date.now();

        // Refresh adaptive spread if stale or not calculated
        if (now - state.lastVwap > this.vwapInterval || state.vwap === null) {
            const adaptive = await this._calculateAdaptiveSpread(symbol);
            if (adaptive) {
                state.vwap = adaptive.mean;  // Store mean for compatibility
                state.stdev = adaptive.stdev;
                state.adaptiveBid = adaptive.maxBid;
                state.adaptiveAsk = adaptive.minAsk;
                state.lastVwap = now;
            }
        }

        // Use adaptive spread if available
        if (state.adaptiveBid !== undefined && state.adaptiveAsk !== undefined) {
            return {
                maxBid: state.adaptiveBid,
                minAsk: state.adaptiveAsk
            };
        }

        // Fallback: if adaptive calculation failed, try simple VWAP spread
        if (state.vwap !== null && state.vwap > 0) {
            const spreadMult = (config.spreadBps || 1) / 10000;
            this._log(`[PRICE] Using fallback VWAP spread for ${symbol}`);
            return {
                maxBid: state.vwap * (1 - spreadMult),
                minAsk: state.vwap * (1 + spreadMult)
            };
        }

        // No price data available
        this._log(`[PRICE] Cannot calculate price range for ${symbol}: no data`);
        return { maxBid: 0, minAsk: Infinity };
    }

    /**
     * Generate quotes for a symbol
     * Ported from usdh.py generate_quotes()
     */
    async _generateQuotes(symbol) {
        const config = this.configs.get(symbol);
        const state = this.state.get(symbol);
        const quotes = { bid: null, ask: null };

        if (!config || !config.enabled) {
            return quotes;
        }

        const { maxBid, minAsk } = await this._getPriceRange(symbol);
        if (maxBid <= 0 || minAsk <= 0) {
            this._log(`[QUOTE] Invalid price range: maxBid=${maxBid}, minAsk=${minAsk}`);
            return quotes;
        }
        // this._log(`[QUOTE] Price range: maxBid=${maxBid.toFixed(5)}, minAsk=${minAsk.toFixed(5)}`);

        const orderbook = state.orderbook;
        if (!orderbook.bids.length || !orderbook.asks.length) {
            this._debug(`[QUOTE] Empty orderbook for ${symbol}`);
            return quotes;
        }

        // Resolve token names dynamically from spotMeta
        let baseTokenName, quoteTokenName;
        try {
            const tokens = await this._resolveSpotPairTokens(symbol);
            baseTokenName = tokens.baseToken;
            quoteTokenName = tokens.quoteToken;
        } catch (error) {
            this._log(`[BALANCE] Error resolving tokens for ${symbol}: ${error.message}`);
            return quotes;
        }

        // Get spot balances directly from Hyperliquid API (like Python usdh.py)
        let availableBase = 0;
        let availableQuote = 0;

        try {
            const spotState = await this._rateLimitedCall(() =>
                this._withTimeout(
                    this.hlClient.infoClient.spotClearinghouseState({ user: this.hlClient.publicAddress }),
                    API_TIMEOUT_MS,
                    'spotClearinghouseState'
                )
            );

            // Parse spot balances - use dynamic token names from spotMeta
            if (spotState && spotState.balances) {
                for (const bal of spotState.balances) {
                    const coin = bal.coin;
                    const total = parseFloat(bal.total || '0');
                    const hold = parseFloat(bal.hold || '0');
                    const available = Math.max(0, total - hold);

                    if (coin === baseTokenName) {
                        availableBase = available * BALANCE_BUFFER;
                    } else if (coin === quoteTokenName) {
                        availableQuote = available * BALANCE_BUFFER;
                    }
                }
            }
        } catch (error) {
            this._log(`[BALANCE] Error fetching spot balances: ${error.message}`);
            return quotes;
        }

        // this._log(`[BALANCE] ${baseTokenName}: ${availableBase.toFixed(4)}, ${quoteTokenName}: ${availableQuote.toFixed(2)}`);

        // Calculate USD values
        const bboBid = orderbook.bids[0][0];
        const bboAsk = orderbook.asks[0][0];

        // Fix #2: Guard against zero/invalid BBO prices from malformed orderbook
        if (bboBid <= 0 || bboAsk <= 0) {
            this._debug(`[QUOTE] Invalid BBO prices: bid=${bboBid}, ask=${bboAsk}`);
            return quotes;
        }

        const estimatedBidPrice = Math.min(bboBid, maxBid);
        const estimatedAskPrice = Math.max(bboAsk, minAsk);

        // Fix #2: Additional guard - estimatedBidPrice must be positive for division
        if (estimatedBidPrice <= 0 || estimatedAskPrice <= 0) {
            this._debug(`[QUOTE] Invalid estimated prices: bid=${estimatedBidPrice}, ask=${estimatedAskPrice}`);
            return quotes;
        }

        // Calculate USD allocation for this pair
        // Priority: balancePct (of total stablecoins) > positionValue (fixed USD)
        let allocationUsd = 0;

        if (config.balancePct && config.balancePct > 0 && config.balancePct <= 100) {
            // Percentage mode: percentage of TOTAL stablecoin balance
            // Fetch total if cache is stale (>10s old)
            if (Date.now() - this._lastBalanceFetch > 10000) {
                await this._fetchTotalStablecoinBalance();
            }
            allocationUsd = this._totalStablecoinBalance * (config.balancePct / 100);
            this._debug(`[QUOTE] Allocation: ${config.balancePct}% of $${this._totalStablecoinBalance.toFixed(2)} total = $${allocationUsd.toFixed(2)}`);
        } else if (config.positionValue > 0) {
            // Fixed USD mode
            allocationUsd = config.positionValue;
            this._debug(`[QUOTE] Allocation: fixed $${allocationUsd.toFixed(2)}`);
        }

        // Each side can use UP TO the allocation (not split)
        // BID: use min(available USDC, allocation)
        // ASK: use min(available base token value, allocation)
        let bidUsd, askUsd;
        if (allocationUsd > 0) {
            bidUsd = Math.min(availableQuote, allocationUsd);
            askUsd = Math.min(availableBase * estimatedAskPrice, allocationUsd);
        } else {
            // No allocation set - use all available
            bidUsd = availableQuote;
            askUsd = availableBase * estimatedAskPrice;
        }

        // Calculate sizes (safe: estimatedBidPrice > 0 validated above)
        let bidSize = bidUsd / estimatedBidPrice;
        let askSize = allocationUsd > 0
            ? Math.min(availableBase, allocationUsd / estimatedAskPrice)
            : availableBase;

        this._debug(`[QUOTE] BBO: ${bboBid.toFixed(5)}/${bboAsk.toFixed(5)}, estimated sizes: bid=${bidSize.toFixed(2)}, ask=${askSize.toFixed(2)}`);

        // Find optimal prices based on queue ratio
        const bidPrice = this._findOptimalBidPrice(orderbook, bidSize, maxBid, config.maxQueueRatio);
        const askPrice = this._findOptimalAskPrice(orderbook, askSize, minAsk, config.maxQueueRatio);

        // Recalculate sizes with final prices
        if (allocationUsd > 0) {
            bidUsd = Math.min(availableQuote, allocationUsd);
            askUsd = Math.min(availableBase * askPrice, allocationUsd);
        } else {
            bidUsd = availableQuote;
            askUsd = availableBase * askPrice;
        }

        this._debug(`[QUOTE] Final prices: bid=${bidPrice.toFixed(5)}, ask=${askPrice.toFixed(5)}`);
        this._debug(`[QUOTE] available USDC=${availableQuote.toFixed(2)}, available USDH=${availableBase.toFixed(4)} ($${askUsd.toFixed(2)})`);
        this._debug(`[QUOTE] bidUsd=${bidUsd.toFixed(2)}, askUsd=${askUsd.toFixed(2)}, min=$${MIN_ORDER_VALUE_USD}`);

        // BID: Buy base with quote
        if (bidUsd >= MIN_ORDER_VALUE_USD) {
            bidSize = bidUsd / bidPrice;
            quotes.bid = { price: bidPrice, size: bidSize };
            this._log(`[QUOTE] BID: ${bidSize.toFixed(4)} @ ${bidPrice.toFixed(5)} ($${bidUsd.toFixed(2)})`);
        } else {
            // this._log(`[QUOTE] BID skipped: $${bidUsd.toFixed(2)} < $${MIN_ORDER_VALUE_USD} min`);
        }

        // ASK: Sell base for quote
        if (askUsd >= MIN_ORDER_VALUE_USD) {
            askSize = askUsd / askPrice;
            quotes.ask = { price: askPrice, size: askSize };
            this._log(`[QUOTE] ASK: ${askSize.toFixed(4)} @ ${askPrice.toFixed(5)} ($${askUsd.toFixed(2)})`);
        } else {
            // this._log(`[QUOTE] ASK skipped: $${askUsd.toFixed(2)} < $${MIN_ORDER_VALUE_USD} min`);
        }

        return quotes;
    }

    /**
     * Check if orders need rebalancing
     * Ported from usdh.py rebalance_check()
     */
    async _checkRebalance(symbol) {
        const config = this.configs.get(symbol);
        const state = this.state.get(symbol);

        if (!config) return false;

        // Get open orders with rate limiting and timeout
        const openOrders = await this._rateLimitedCall(() =>
            this._withTimeout(
                this.hlClient.getSpotOpenOrders(),
                API_TIMEOUT_MS,
                'getSpotOpenOrders'
            )
        );
        const symbolOrders = openOrders.filter(o => o.coin === symbol);

        if (!symbolOrders.length) {
            return false;
        }

        const { maxBid, minAsk } = await this._getPriceRange(symbol);
        if (maxBid <= 0 || minAsk <= 0) return false;

        const orderbook = state.orderbook;
        if (!orderbook.bids.length || !orderbook.asks.length) return false;

        // Resolve token names dynamically from spotMeta
        let baseTokenName, quoteTokenName;
        try {
            const tokens = await this._resolveSpotPairTokens(symbol);
            baseTokenName = tokens.baseToken;
            quoteTokenName = tokens.quoteToken;
        } catch (error) {
            this._log(`[REBALANCE] Error resolving tokens for ${symbol}: ${error.message}`);
            return false;
        }

        // Get current balances directly from API (like Python usdh.py)
        let availableBase = 0;
        let availableQuote = 0;

        try {
            const spotState = await this._rateLimitedCall(() =>
                this._withTimeout(
                    this.hlClient.infoClient.spotClearinghouseState({ user: this.hlClient.publicAddress }),
                    API_TIMEOUT_MS,
                    'spotClearinghouseState'
                )
            );

            if (spotState && spotState.balances) {
                for (const bal of spotState.balances) {
                    const coin = bal.coin;
                    const total = parseFloat(bal.total || '0');
                    const hold = parseFloat(bal.hold || '0');
                    const available = Math.max(0, total - hold);

                    if (coin === baseTokenName) {
                        availableBase = available * BALANCE_BUFFER;
                    } else if (coin === quoteTokenName) {
                        availableQuote = available * BALANCE_BUFFER;
                    }
                }
            }
        } catch (error) {
            this._log(`[REBALANCE] Error fetching spot balances: ${error.message}`);
            return false;
        }

        const PRICE_EPSILON = 0.00001;
        const MIN_NEW_FUNDS_RATIO = 0.10;

        let cancelledAny = false;

        for (const order of symbolOrders) {
            const orderSide = order.side;
            const orderSize = order.size;
            const orderPrice = order.price;
            const oid = order.oid;

            if (orderSize <= 0 || orderPrice <= 0) continue;

            if (orderSide === 'B') {  // Buy order
                // Calculate optimal price for this order's size
                const optimalPrice = this._findOptimalBidPrice(orderbook, orderSize, maxBid, config.maxQueueRatio);

                // Check if order price is no longer optimal
                if (Math.abs(orderPrice - optimalPrice) > PRICE_EPSILON) {
                    this._log(`[REBALANCE] BID queue ratio stale: current=${orderPrice.toFixed(5)}, optimal=${optimalPrice.toFixed(5)}`);
                    await this._cancelOrder(symbol, oid);
                    cancelledAny = true;
                    continue;
                }

                // Check if we have significant extra funds
                const wouldPlaceSize = availableQuote / optimalPrice;
                if (orderSize > 0 && wouldPlaceSize > 0) {
                    const totalCapital = orderSize + wouldPlaceSize;
                    const idleRatio = wouldPlaceSize / totalCapital;
                    if (idleRatio > MIN_NEW_FUNDS_RATIO) {
                        this._log(`[REBALANCE] BID: ${(idleRatio * 100).toFixed(1)}% idle (+${wouldPlaceSize.toFixed(2)}), resizing to ${totalCapital.toFixed(2)}`);
                        await this._cancelOrder(symbol, oid);
                        cancelledAny = true;
                    }
                }
            } else if (orderSide === 'A') {  // Sell order
                // Calculate optimal price for this order's size
                const optimalPrice = this._findOptimalAskPrice(orderbook, orderSize, minAsk, config.maxQueueRatio);

                // Check if order price is no longer optimal
                if (Math.abs(orderPrice - optimalPrice) > PRICE_EPSILON) {
                    this._log(`[REBALANCE] ASK queue ratio stale: current=${orderPrice.toFixed(5)}, optimal=${optimalPrice.toFixed(5)}`);
                    await this._cancelOrder(symbol, oid);
                    cancelledAny = true;
                    continue;
                }

                // Check if we have significant extra funds (availableBase already computed above)
                if (orderSize > 0 && availableBase > 0) {
                    const totalCapital = orderSize + availableBase;
                    const idleRatio = availableBase / totalCapital;
                    if (idleRatio > MIN_NEW_FUNDS_RATIO) {
                        this._log(`[REBALANCE] ASK: ${(idleRatio * 100).toFixed(1)}% idle (+${availableBase.toFixed(2)}), resizing to ${totalCapital.toFixed(2)}`);
                        await this._cancelOrder(symbol, oid);
                        cancelledAny = true;
                    }
                }
            }
        }

        return cancelledAny;
    }

    // === ORDER MANAGEMENT ===

    /**
     * Cancel a single order
     */
    async _cancelOrder(symbol, oid) {
        try {
            const result = await this.hlClient.spotCancel(symbol, oid);
            if (result.success) {
                this._log(`Cancelled order ${oid}`);
                return true;
            } else {
                this._log(`Failed to cancel order ${oid}: ${result.error}`);
                return false;
            }
        } catch (error) {
            this._log(`Error cancelling order ${oid}: ${error.message}`);
            return false;
        }
    }

    /**
     * Cancel all orders for a symbol
     */
    async _cancelAll(symbol) {
        try {
            const openOrders = await this.hlClient.getSpotOpenOrders();
            const symbolOrders = openOrders.filter(o => o.coin === symbol);

            let cancelled = 0;
            for (const order of symbolOrders) {
                if (await this._cancelOrder(symbol, order.oid)) {
                    cancelled++;
                }
            }

            this._log(`Cancelled ${cancelled}/${symbolOrders.length} orders for ${symbol}`);
            return cancelled;
        } catch (error) {
            this._log(`Error cancelling all orders for ${symbol}: ${error.message}`);
            return 0;
        }
    }

    /**
     * Place an order
     */
    async _placeOrder(symbol, side, size, price) {
        try {
            const isBuy = side === 'buy';
            const result = await this.hlClient.spotOrder(symbol, isBuy, size, price);

            if (result.success) {
                this._log(`Placed ${side.toUpperCase()}: ${size.toFixed(4)} @ ${price.toFixed(5)} (oid=${result.oid})`);
                return result.oid;
            } else {
                this._log(`Failed to place ${side.toUpperCase()}: ${result.error}`);
                return null;
            }
        } catch (error) {
            this._log(`Error placing ${side} order: ${error.message}`);
            return null;
        }
    }

    // === MAIN LOOP ===

    /**
     * Start the market maker
     */
    start() {
        if (this._isRunning) {
            this._log('Already running');
            return;
        }

        if (!this.hlClient || !this.hlClient.isInitialized) {
            this._log('Cannot start: Hyperliquid client not initialized');
            return;
        }

        if (this.configs.size === 0) {
            this._log('Cannot start: No symbols configured');
            return;
        }

        this._isRunning = true;
        this.enabled = true;
        this._log('Starting spot market maker...');

        // Initialize WebSocket for real-time updates
        this._initWebSocket();

        // Run immediately, then on interval (5s - REST is fallback only)
        this._runLoop();
        this._loopInterval = setInterval(() => this._runLoop(), this.loopDelay);
        
        // Persist running state
        this.saveState();
    }

    /**
     * Stop the market maker
     */
    async stop() {
        this._log('Stopping spot market maker...');
        this.enabled = false;
        this._isRunning = false;

        if (this._loopInterval) {
            clearInterval(this._loopInterval);
            this._loopInterval = null;
        }

        // Close WebSocket
        this._closeWebSocket();

        // Cancel all orders
        for (const symbol of this.configs.keys()) {
            await this._cancelAll(symbol);
        }

        this._log('Stopped');
        
        // Persist stopped state
        this.saveState();
    }

    /**
     * Main loop iteration
     * Fix #1: Rate limiting on all API calls
     * Fix #3: Timeout on all API calls
     * Fix #5: Uses mutex to prevent concurrent execution
     * Fix #7: Re-fetches orders after rebalance to avoid stale state
     */
    async _runLoop() {
        if (!this.enabled) return;

        // Fix #5: Prevent concurrent loop execution
        if (this._loopInProgress) {
            return;
        }
        this._loopInProgress = true;

        try {
            for (const [symbol, config] of this.configs) {
                if (!config.enabled) continue;

                const state = this.state.get(symbol);
                const now = Date.now();

                // 1. Poll orderbook if stale (with rate limiting and timeout)
                if (now - state.lastOrderbook >= this.orderbookInterval || !state.orderbook.bids.length) {
                    try {
                        state.orderbook = await this._rateLimitedCall(() =>
                            this._withTimeout(
                                this.hlClient.fetchSpotOrderbook(symbol, 20),
                                API_TIMEOUT_MS,
                                'fetchSpotOrderbook'
                            )
                        );
                        state.lastOrderbook = now;

                        if (state.orderbook.bids.length > 0 && state.orderbook.asks.length > 0) {
                            const bboBid = state.orderbook.bids[0][0];
                            const bboAsk = state.orderbook.asks[0][0];
                            // this._log(`[ORDERBOOK] ${symbol} BBO: ${bboBid.toFixed(5)} / ${bboAsk.toFixed(5)}, depth: ${state.orderbook.bids.length} bids, ${state.orderbook.asks.length} asks`);
                        }
                    } catch (error) {
                        this._log(`Failed to fetch orderbook for ${symbol}: ${error.message}`);
                    }
                }

                // 2. Skip if orderbook invalid
                if (!state.orderbook.bids.length || !state.orderbook.asks.length) {
                    continue;
                }

                // 3. Check open orders (with rate limiting and timeout)
                let openOrders = await this._rateLimitedCall(() =>
                    this._withTimeout(
                        this.hlClient.getSpotOpenOrders(),
                        API_TIMEOUT_MS,
                        'getSpotOpenOrders'
                    )
                );
                let symbolOrders = openOrders.filter(o => o.coin === symbol);
                let hasBid = symbolOrders.some(o => o.side === 'B');
                let hasAsk = symbolOrders.some(o => o.side === 'A');

                // 4. Periodic rebalance check
                if (now - state.lastRebalance >= this.rebalanceInterval) {
                    if (symbolOrders.length > 0) {
                        const cancelled = await this._checkRebalance(symbol);
                        if (cancelled) {
                            // Re-fetch orders after cancel to get fresh state
                            openOrders = await this._rateLimitedCall(() =>
                                this._withTimeout(
                                    this.hlClient.getSpotOpenOrders(),
                                    API_TIMEOUT_MS,
                                    'getSpotOpenOrders'
                                )
                            );
                            symbolOrders = openOrders.filter(o => o.coin === symbol);
                            hasBid = symbolOrders.some(o => o.side === 'B');
                            hasAsk = symbolOrders.some(o => o.side === 'A');
                        }
                    }
                    state.lastRebalance = now;
                }

                // 5. If both orders exist, wait
                if (hasBid && hasAsk) {
                    continue;
                }

                // 6. Generate quotes for missing orders
                const quotes = await this._generateQuotes(symbol);

                // 7. Place missing orders
                if (!hasBid && quotes.bid) {
                    await this._placeOrder(symbol, 'buy', quotes.bid.size, quotes.bid.price);
                }

                if (!hasAsk && quotes.ask) {
                    await this._placeOrder(symbol, 'sell', quotes.ask.size, quotes.ask.price);
                }
            }
        } catch (error) {
            this._log(`Loop error: ${error.message}`);
        } finally {
            // Fix #5: Always release mutex
            this._loopInProgress = false;
        }
    }

    // === LOGGING ===

    _log(...args) {
        const timestamp = new Date().toISOString();
        const message = args.map(a => typeof a === 'object' ? JSON.stringify(a) : a).join(' ');
        const logEntry = `[${timestamp}] ${message}`;

        this.logs.push(logEntry);
        if (this.logs.length > this.maxLogs) {
            this.logs.shift();
        }

        // console.log('[SPOT]', ...args);
    }

    /**
     * Debug logging - configurable via setDebugEnabled()
     * Fix #15: Debug logging can now be enabled via config
     */
    _debug(...args) {
        if (this._debugEnabled) {
            const timestamp = new Date().toISOString();
            console.log(`[SPOT-DEBUG ${timestamp}]`, ...args);
        }
    }

    /**
     * Enable/disable debug logging
     * Fix #15: Added method to control debug output
     */
    setDebugEnabled(enabled) {
        this._debugEnabled = enabled;
        this._log(`Debug logging ${enabled ? 'enabled' : 'disabled'}`);
    }

    // === PUBLIC API ===

    /**
     * Get current status
     * Fix #7: Removed dead openOrders reference
     */
    getStatus() {
        const status = {
            enabled: this.enabled,
            isRunning: this._isRunning,
            debugEnabled: this._debugEnabled,
            symbols: {}
        };

        for (const [symbol, config] of this.configs) {
            const state = this.state.get(symbol);

            // Get adaptive bid/ask for display (mean ± 1.5σ)
            // Use cached adaptive values if available, otherwise fall back to simple VWAP spread
            let vwapBid = state?.adaptiveBid ?? null;
            let vwapAsk = state?.adaptiveAsk ?? null;

            // Fallback to simple VWAP spread if adaptive not calculated yet
            if ((vwapBid === null || vwapAsk === null) && state?.vwap > 0) {
                const spreadMult = (config.spreadBps || 1) / 10000;
                vwapBid = state.vwap * (1 - spreadMult);
                vwapAsk = state.vwap * (1 + spreadMult);
            }

            status.symbols[symbol] = {
                config,
                lastOrderbook: state?.lastOrderbook || 0,
                lastRebalance: state?.lastRebalance || 0,
                vwap: state?.vwap ?? null,  // Mean price (for reference)
                stdev: state?.stdev ?? null, // Standard deviation (for reference)
                lastVwap: state?.lastVwap || 0,
                vwapBid,   // Adaptive bid (mean - 1.5σ) for UI display
                vwapAsk    // Adaptive ask (mean + 1.5σ) for UI display
            };
        }

        return status;
    }

    /**
     * Get logs
     */
    getLogs(count = 100) {
        return this.logs.slice(-count);
    }

    /**
     * Save state to localStorage for persistence across sessions
     */
    saveState() {
        // Skip saving during restoration to avoid recursion
        if (this._isRestoring) return;
        
        try {
            const stateData = {
                configs: Array.from(this.configs.entries()),
                isRunning: this._isRunning
            };
            localStorage.setItem('spotHandlerState', JSON.stringify(stateData));
        } catch (error) {
            this._log(`Error saving state: ${error.message}`);
        }
    }

    /**
     * Restore state from localStorage
     */
    restoreState() {
        this._isRestoring = true;
        
        try {
            const saved = localStorage.getItem('spotHandlerState');
            if (!saved) {
                this._isRestoring = false;
                return;
            }

            const stateData = JSON.parse(saved);
            
            // Restore configs
            if (stateData.configs && Array.isArray(stateData.configs)) {
                for (const [symbol, config] of stateData.configs) {
                    this.addSymbol(symbol, config);
                }
                this._log(`Restored ${stateData.configs.length} MM pairs`);
            }

            // Auto-restart if was running
            if (stateData.isRunning && this.configs.size > 0) {
                this._log('Auto-restarting MM bot from previous session');
                setTimeout(() => this.start(), 1000);
            }
        } catch (error) {
            this._log(`Error restoring state: ${error.message}`);
        } finally {
            this._isRestoring = false;
        }
    }
}

// Export singleton
if (typeof window !== 'undefined') {
    window.SpotHandler = new SpotHandler();
}

export default SpotHandler;
